import React, { useMemo } from 'react';
import moment from 'moment';
import { BaseCard } from '../BaseCard';
import { QuestionCircleFilled } from '@ant-design/icons';
import { Chart } from './Chart';
import _, { capitalize } from 'lodash';
import './CardMobileData.scss';

/**
 * @typedef {object} IEvent
 * @property {{time: number, avgPeopleDensity: number, totalPeopleDensity: number}} summary
 * @property {{hour: number | undefined, time: string, counts_event: number}[]} timeSeries
 */

/**
 * @typedef {object} IUnique
 * @property {{time: number, avgPeopleDensity: number, totalPeopleDensity: number}} summary
 * @property {{hour: number | undefined, time: string, counts: number}[]} timeSeries
 */

/**
 * @param {object} props
 * @param {'Event'|'Unique'} props.type
 * @param {'daily'|'monthly'|'yearly'} props.periodic
 * @param {string} props.total
 * @param {string} props.average
 * @param {string} props.color
 */
const CardSummary = (props) => {
  const style = {
    backgroundColor: props.color,
    borderRadius: '100%',
    minWidth: '0.5rem',
    minHeight: '0.5rem',
  };
  const getTextTitle = ({ type, summary }) => {
    const typeEvent = type ? String(type).toLowerCase() : '';
    if (typeEvent === 'event') {
      if (summary === 'total') {
        return `Total ${props.periodic} Activity`;
      }
      if (summary === 'avg') {
        return `${capitalize(summary)}. ${props.periodic} Activity`;
      }
    }
    if (typeEvent === 'unique') {
      if (summary === 'total') {
        return `Total ${props.periodic} Unique Devices`;
      }
      if (summary === 'avg') {
        return `${capitalize(summary)}. ${props.periodic} Unique Devices`;
      }
    }
    return '';
  };

  return (
    <div className='SharedCardMobileData-summary'>
      <div className='SharedCardMobileData-summary-card'>
        <div className='title-wrapper'>
          <div style={style} />
          <div className='text'>
            {getTextTitle({ type: props.type, summary: 'total' })}
          </div>
        </div>

        <div className='sub-text'>{props?.total}</div>
      </div>
      <div className='SharedCardMobileData-summary-card'>
        <div className='title-wrapper'>
          <div style={style} />
          <div className='text'>
            {getTextTitle({ type: props.type, summary: 'avg' })}
          </div>
        </div>
        <div className='sub-text'>{props.average}</div>
      </div>
    </div>
  );
};

/**
 * @param {object} props
 * @param {string} props.title
 * @param {boolean} props.isLoading
 * @param {object} props.style
 * @param {{event: IEvent, unique: IUnique}} props.data
 * @param {{main: string, secondary: string}} props.colorUnique
 * @param {{main: string, secondary: string}} props.colorEvent
 * @param {'daily'|'monthly'|'yearly'} props.periodic
 */
export const CardMobileData = (props) => {
  const { style, isLoading, title, periodic, colorUnique, colorEvent, data } =
    props;

  const getChartType = () => {
    const chartTypes = {
      daily: { chart: 'hourly', type: 'Hourly', format: 'DD MMMM YYYY' },
      monthly: { chart: 'daily', type: 'Daily', format: 'MMMM, YYYY' },
      yearly: { chart: 'monthly', type: 'Monthly', format: 'YYYY' },
    };
    return chartTypes[periodic] || { chart: '', type: '' };
  };

  const chartType = useMemo(() => getChartType(), [periodic]);
  const eventTime = data?.event?.summary?.time;
  const uniqueTime = data?.unique?.summary?.time;
  const momentEvent = moment(eventTime?.toString());
  const momentUnique = moment(uniqueTime?.toString());
  return (
    <div className='SharedCardMobileData' style={style}>
      {/* Recorder Activity */}
      <BaseCard
        className='CardMobileData'
        isLoading={isLoading}
        title={title}
        withPadding={true}
      >
        <div className='SharedCardMobileData-wrapper'>
          {/* Header Title */}
          <div className='SharedCardMobileData-wrapper-header'>
            <div className='SharedCardMobileData-wrapper-header-text'>
              Recorded Activity
            </div>
            <div className='SharedCardMobileData-wrapper-header-sub-text'>
              {(momentEvent.isValid() ? momentEvent : moment(eventTime)).format(
                chartType.format
              )}
            </div>
          </div>
          {/* Card Summary */}
          <CardSummary
            average={(_.isEmpty(data?.event?.summary?.avgPeopleDensity)
              ? data?.event?.summary?.avgPeopleDensity
              : 'N/A'
            )?.toLocaleString('id-ID')}
            color={colorEvent.main}
            periodic={chartType.type}
            total={(_.isEmpty(data?.event?.summary?.totalPeopleDensity)
              ? data?.event?.summary?.totalPeopleDensity
              : 'N/A'
            )?.toLocaleString('id-ID')}
            type='Event'
          />
          {/* Chart */}
          <div className='SharedCardMobileData-chart-wrapper'>
            <div className='SharedCardMobileData-title-wrapper'>
              <div className='SharedCardMobileData-title'>
                {chartType.type} Recorded Activity
              </div>
              <QuestionCircleFilled className='SharedCardMobileData-title-btn' />
            </div>
            <div className='SharedCardMobileData-chart'>
              <div className='SharedCardMobileData-chart-title'>
                {(momentEvent.isValid()
                  ? momentEvent
                  : moment(eventTime)
                ).format(chartType.format)}
              </div>
              <div className='SharedCardMobileData-chart-data'>
                <Chart
                  color={colorEvent}
                  data={data?.event?.timeSeries}
                  type={chartType.chart}
                />
              </div>
            </div>
          </div>
        </div>
      </BaseCard>

      {/* Unique Devices */}
      <div className='shared BaseCard'>
        <div className='SharedCardMobileData-wrapper'>
          {/* Header Title */}
          <div className='SharedCardMobileData-wrapper-header'>
            <div className='SharedCardMobileData-wrapper-header-text'>
              Unique Devices
            </div>
            <div className='SharedCardMobileData-wrapper-header-sub-text'>
              {(momentUnique.isValid()
                ? momentUnique
                : moment(momentUnique)
              ).format(chartType.format)}
            </div>
          </div>
          {/* Card Summary */}
          <CardSummary
            average={(_.isEmpty(data?.unique?.summary?.avgPeopleDensity)
              ? data?.unique?.summary?.avgPeopleDensity
              : 'N/A'
            )?.toLocaleString('id-ID')}
            color={colorUnique.main}
            periodic={chartType.type}
            total={(_.isEmpty(data?.unique?.summary?.totalPeopleDensity)
              ? data?.unique?.summary?.totalPeopleDensity
              : 'N/A'
            )?.toLocaleString('id-ID')}
            type='Unique'
          />
          {/* Chart */}
          <div className='SharedCardMobileData-chart-wrapper'>
            <div className='SharedCardMobileData-title-wrapper'>
              <div className='SharedCardMobileData-title'>
                {chartType.type} Unique Devices
              </div>
              <QuestionCircleFilled className='SharedCardMobileData-title-btn' />
            </div>
            <div className='SharedCardMobileData-chart'>
              <div className='SharedCardMobileData-chart-title'>
                {(momentUnique.isValid()
                  ? momentUnique
                  : moment(uniqueTime)
                ).format(chartType.format)}
              </div>
              <div className='SharedCardMobileData-chart-data'>
                <Chart
                  color={colorUnique}
                  data={data?.unique?.timeSeries}
                  type={chartType.chart}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
